import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { API_URL, auth, errMsg, rand } from '../financeApi';
import { monthName, monthOptions } from './payShared';

const MonthSelect = ({ value, onChange, center, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent className="max-h-64">
      {monthOptions(center).map((m) => <SelectItem key={m} value={m}>{monthName(m)}</SelectItem>)}
    </SelectContent>
  </Select>
);

// target: { kid, month }
export const FeeDialog = ({ target, onClose, token, onDone }) => {
  const [mode, setMode] = useState('normal');
  const [amount, setAmount] = useState('');
  const [month, setMonth] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!target) return;
    setMode('normal');
    setAmount(target.kid.normal_fee != null ? String(target.kid.normal_fee) : '');
    setMonth(target.month);
    setNote('');
  }, [target]);

  const send = async (remove = false) => {
    setBusy(true);
    try {
      const url = `${API_URL}/finance/parent-payments/kids/${target.kid.kid_id}/${mode === 'normal' ? 'fee' : 'fee-override'}`;
      const body = mode === 'normal' ? { amount: Number(amount), from_month: month } : { month, amount: remove ? null : Number(amount), note, remove };
      const res = await axios.put(url, body, auth(token));
      toast.success(res.data.message);
      onClose();
      onDone();
    } catch (e) {
      toast.error(errMsg(e, 'Could not save the fee'));
    } finally {
      setBusy(false);
    }
  };

  const kid = target?.kid;
  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="fee-dialog">
        <DialogHeader>
          <DialogTitle>Monthly fee · {kid?.full_name}</DialogTitle>
          <DialogDescription>Normal fee now: {kid?.normal_fee != null ? rand(kid.normal_fee) : 'not set'}. Every change is kept in the log.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          {[['normal', 'Normal monthly fee'], ['special', 'One month only']].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setMode(v)} data-testid={`fee-mode-${v}`}
              className={`rounded-xl border p-2.5 text-sm transition-colors ${mode === v ? 'border-emerald-600 bg-emerald-50 text-emerald-800 font-semibold' : 'hover:bg-muted/40'}`}>{l}</button>
          ))}
        </div>
        <div className="space-y-4">
          <div>
            <Label>{mode === 'normal' ? 'Starting from' : 'Which month'}</Label>
            {target && <MonthSelect value={month} onChange={setMonth} center={target.month} testid="fee-month-select" />}
            {mode === 'normal' && <p className="text-xs text-muted-foreground mt-1">Months before this keep their old fee.</p>}
          </div>
          <div>
            <Label htmlFor="fee-amount">Fee (R)</Label>
            <Input id="fee-amount" type="number" inputMode="decimal" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="fee-amount-input" />
          </div>
          {mode === 'special' && (
            <div>
              <Label htmlFor="fee-note">Reason (optional)</Label>
              <Input id="fee-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Sibling discount" data-testid="fee-note-input" />
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          {mode === 'special' && month === target?.month && kid?.month?.special_fee && (
            <Button variant="ghost" className="text-red-600" disabled={busy} onClick={() => send(true)} data-testid="fee-remove-special-btn">Remove special fee</Button>
          )}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || amount === '' || Number(amount) < 0 || !month} onClick={() => send(false)} data-testid="fee-save-btn">Save fee</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
